// Pure helpers for reading what Supabase Auth appends to our return URL.
//
// Supabase reports a failed redirect (expired/used email link, denied OAuth)
// either in the query string (?error=...) or in the URL fragment
// (#error=...&error_code=...&error_description=...). The fragment is also
// where HashRouter keeps its routes (#/mis-equipos), so a fragment only counts
// as Auth params when it is not a route and carries a known Auth key.

/** Query marker added to password-reset links (see requestPasswordReset). */
export const RESET_PARAM = "reset";

export const RECOVERY_LINK_ERROR =
  "El enlace para cambiar tu contraseña expiró o ya se usó. " +
  "Cada enlace sirve una sola vez: pide uno nuevo con «¿Olvidaste tu contraseña?».";

export const EMAIL_LINK_ERROR =
  "El enlace del correo expiró o ya se usó. Pide uno nuevo.";

const ERROR_KEYS = ["error", "error_code", "error_description"];
// Never consumed (PKCE uses ?code=), but must not linger in the address bar.
const TOKEN_KEYS = ["access_token", "refresh_token", "provider_token"];

// Error codes Supabase uses for an email link that can no longer be used.
const EXPIRED_LINK_CODES = new Set(["otp_expired", "flow_state_expired", "flow_state_not_found"]);

function decode(value) {
  if (!value) return "";
  return String(value).replace(/\+/g, " ").trim();
}

function readError(params) {
  if (!ERROR_KEYS.some((k) => params.has(k))) return null;
  return {
    error: decode(params.get("error")),
    code: decode(params.get("error_code")),
    description: decode(params.get("error_description")),
  };
}

/** Fragment params, or null when the fragment is a HashRouter route or empty. */
function authFragmentParams(hash) {
  const raw = String(hash || "").replace(/^#/, "");
  if (!raw || raw.startsWith("/")) return null;
  const params = new URLSearchParams(raw);
  const keys = [...ERROR_KEYS, ...TOKEN_KEYS];
  return keys.some((k) => params.has(k)) ? params : null;
}

/**
 * Reads the Auth return state from location.search and location.hash.
 * Returns { code, flowId, recovery, error, hasAuthParams } where error is
 * { error, code, description, source } or null. Tokens are never returned.
 */
export function parseAuthReturn(search, hash) {
  const query = new URLSearchParams(search || "");
  const fragment = authFragmentParams(hash);

  const queryError = readError(query);
  const fragmentError = fragment ? readError(fragment) : null;
  const error = queryError
    ? { ...queryError, source: "query" }
    : fragmentError
    ? { ...fragmentError, source: "hash" }
    : null;

  const code = query.get("code") || null;
  return {
    code,
    flowId: query.get("sb_flow_id") || null,
    recovery: query.get(RESET_PARAM) === "1",
    error,
    hasAuthParams: Boolean(code || queryError || fragment),
  };
}

/** Spanish message for an Auth error returned in the URL. */
export function authErrorMessage(error, { recovery = false } = {}) {
  if (!error) return null;
  if (recovery) return RECOVERY_LINK_ERROR;
  if (EXPIRED_LINK_CODES.has(error.code)) return EMAIL_LINK_ERROR;
  return error.description || error.error || "No se pudo completar el inicio de sesión.";
}
